import { Mic, MessageCircle, Phone, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useVoiceAssistant } from "@/hooks/useVoiceAssistant";
import { COMPANY, getPhoneLink } from "@/config/company";
import { AIChatDialog } from "@/components/ai/AIChatDialog";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

export function CTASection() {
  const { isConnecting, isActive, toggleConversation, assistantState } = useVoiceAssistant();
  const { trackCTAClick, trackPhoneClick } = useAnalyticsContext();

  return (
    <section id="contact" className="py-16 lg:py-24 bg-secondary relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-accent/20 backdrop-blur-sm border border-accent/30 rounded-full px-4 py-2 mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
            </span>
            <span className="text-sm font-medium text-secondary-foreground">Care Coordinators Available Now</span>
          </div>

          {/* Headline */}
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-foreground mb-6">
            Start Your Health Journey Today
          </h2>

          {/* Subheadline */}
          <p className="text-secondary-foreground/80 text-lg mb-10 max-w-xl mx-auto">
            Get a free consultation and personalized treatment quote. Our AI assistant and care coordinators are ready to help you 24/7.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Button 
              onClick={() => {
                trackCTAClick('Talk to AI Assistant', 'cta_section', 'voice');
                toggleConversation();
              }}
              disabled={isConnecting}
              className="rounded-full px-8 py-6 font-medium gap-3 text-base bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Calling...
                </>
              ) : isActive ? (
                <>
                  <Mic className="w-5 h-5 animate-pulse" />
                  {assistantState === "speaking" ? "AI Speaking..." : "AI Listening..."}
                </>
              ) : (
                <>
                  <Mic className="w-5 h-5" />
                  Talk to AI Assistant
                </>
              )}
            </Button>
            <AIChatDialog
              context="User interested in getting a free consultation and treatment quote"
              className="rounded-full px-8 py-6 font-medium gap-3 text-base border-2 border-secondary-foreground/30 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent"
            />
          </div>

          {/* Phone CTA */}
          <div className="flex items-center justify-center gap-3 text-secondary-foreground/80">
            <Phone className="w-5 h-5" />
            <span>Or call us directly:</span>
            <a 
              href={getPhoneLink()} 
              className="font-semibold text-accent hover:underline"
              onClick={() => trackPhoneClick('cta_section')}
            >
              {COMPANY.phone.display}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
